import { Flex } from 'components';
import { InfoTooltip } from 'components/InfoTooltip/InfoTooltip';
import { Button, FieldLabel, TextField } from 'components/_form';
import { ErrorText } from 'components/_form/ErrorText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IPostAuditFormRequest } from 'types/audits';
import { IPractice } from 'types/methologies';
import { AuditFormCodeBlock } from './AuditFormCodeBlock';
import { EditAuditFormCodeModal } from './EditAuditFormCodeModal';

interface Props {
  practices: IPractice[];
}

export const AuditFormCodeNameField: React.FC<Props> = ({ practices }) => {
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const {
    register,
    watch,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext<IPostAuditFormRequest['audit_form']>();

  const pinnedToType = watch('pinned_to_type');
  const pinnedToId = watch('pinned_to_id');
  const codeId = watch('id_code') ?? '';
  const colorCode = watch('color_code') ?? '';

  const initialPinHandled = useRef(false);
  const prevPinKey = useRef<string | undefined>();

  useEffect(() => {
    const pinKey =
      pinnedToType && pinnedToId
        ? `${pinnedToType}:${pinnedToId}`
        : undefined;

    const inheritFromPractice = (id: string) => {
      const practice = practices.find(
        ({ id: practiceIdValue }) => String(practiceIdValue) === id
      );

      if (practice) {
        setValue('id_code', practice.id_code ?? '', { shouldDirty: true });
        setValue('color_code', practice.color_code ?? '', { shouldDirty: true });
      }
    };

    if (!initialPinHandled.current) {
      initialPinHandled.current = true;
      prevPinKey.current = pinKey;
      const { id_code, color_code } = getValues();

      if (
        pinnedToType === 'Practice' &&
        pinnedToId &&
        !id_code &&
        !color_code
      ) {
        inheritFromPractice(String(pinnedToId));
      }

      return;
    }

    if (prevPinKey.current === pinKey) return;
    prevPinKey.current = pinKey;

    if (!pinKey) {
      setValue('id_code', '', { shouldDirty: true });
      setValue('color_code', '', { shouldDirty: true });
      return;
    }

    // Kod/kolor dziedziczymy tylko z praktyki; metodyka nie ma tych pól.
    if (pinnedToType === 'Practice' && pinnedToId) {
      inheritFromPractice(String(pinnedToId));
    } else {
      setValue('id_code', '', { shouldDirty: true });
      setValue('color_code', '', { shouldDirty: true });
    }
  }, [pinnedToType, pinnedToId, practices, getValues, setValue]);

  return (
    <>
      <input type="hidden" {...register('id_code')} />
      <input type="hidden" {...register('color_code')} />

      <Flex width={['100%', '35%']} flexDirection="column">
        <Flex alignItems="center" gap="5px">
          <FieldLabel>{t('administrationAuditsView.codeAndFormName')}</FieldLabel>
          <InfoTooltip
            tooltipId="audit-form-name-grouping-tooltip"
            tooltipValue="administrationAuditsView.formNameGroupingTooltip"
            tooltipWidth="300px"
          />
        </Flex>
        <Flex gap="15px" alignItems="end">
          <AuditFormCodeBlock codeId={codeId} colorCode={colorCode} />

          <Flex flex={1} flexDirection="column">
            <TextField
              {...register('name')}
              error={t(errors?.name?.message || '')}
            />
          </Flex>

          <Button
            variant="eucalyptus"
            icon={<FontAwesomeIcon icon={faEdit} size="xl" />}
            onClick={() => setIsEditModalOpen(true)}
          />
        </Flex>
        {(errors?.id_code || errors?.color_code) && (
          <ErrorText>
            {t(
              errors?.id_code?.message ||
                errors?.color_code?.message ||
                'common.fieldRequired'
            )}
          </ErrorText>
        )}
      </Flex>

      <EditAuditFormCodeModal
        isOpen={isEditModalOpen}
        onCancelClick={() => setIsEditModalOpen(false)}
        codeId={codeId}
        colorCode={colorCode || '#cccccc'}
        onSave={({ id_code, color_code }) => {
          setValue('id_code', id_code, { shouldDirty: true, shouldValidate: true });
          setValue('color_code', color_code, {
            shouldDirty: true,
            shouldValidate: true
          });
        }}
      />
    </>
  );
};
